import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import PropTypes from 'prop-types';
import { useField } from 'react-final-form';

import colors from 'common/constants/colors';

import {
  formatOnFileValueForEditing,
  isContactFieldChanged,
} from '../../utils/partnerContactsForm';

const PLACEHOLDER_EMAIL_NOTE = 'Placeholder address — never emailed';

const editedFieldSx = {
  '& .MuiOutlinedInput-root': { bgcolor: colors.editedFieldBlue },
  '& .MuiOutlinedInput-root:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.editedFieldBlueBorder,
  },
  '& .MuiInputLabel-root:not(.Mui-focused)': { color: colors.editedFieldBlueLabel },
};

const PartnerContactField = ({
  id,
  name,
  contactField,
  onFileValue,
  hasPlaceholderOnFile,
  isUnconfirmedRemoval,
  isDisabled,
  clearLabel,
  onClear,
  inputRef,
}) => {
  const { fieldKind, label, noun, emptyPlaceholder, inputType, validate } = contactField;
  // A value nobody changed is never re-checked, so an odd value on file cannot block saving
  // another partner.
  const { input, meta } = useField(name, {
    validate: (value) =>
      isContactFieldChanged({ fieldKind, value, onFileValue }) ? validate(value) : undefined,
  });

  const isChanged = isContactFieldChanged({ fieldKind, value: input.value, onFileValue });
  const onFileDisplayValue = formatOnFileValueForEditing(fieldKind, onFileValue);
  const errorMessage =
    (meta.touched && meta.error) || (!meta.dirtySinceLastSubmit && meta.submitError) || '';
  const isPlaceholderWarningShown = hasPlaceholderOnFile && !isChanged && !errorMessage;
  const isClearShown = !!input.value && !isDisabled;
  const placeholderNoteId = `${id}-placeholder-note`;

  const describeFieldState = () => {
    if (errorMessage) return errorMessage;
    // The removal confirmation under the field already says what was there.
    if (isUnconfirmedRemoval) return '';
    if (isChanged) {
      return onFileDisplayValue ? `Was ${onFileDisplayValue}` : `New — no ${noun} on file before`;
    }

    return '';
  };

  return (
    <TextField
      id={id}
      name={input.name}
      label={label}
      type={inputType}
      value={input.value}
      onChange={input.onChange}
      onBlur={input.onBlur}
      onFocus={input.onFocus}
      inputRef={inputRef}
      placeholder={emptyPlaceholder}
      disabled={isDisabled}
      error={!!errorMessage}
      helperText={describeFieldState() || undefined}
      size='small'
      fullWidth
      InputLabelProps={{ shrink: true }}
      InputProps={{
        // The placeholder note sits behind a warning icon; screen readers get it as the description.
        ...(isPlaceholderWarningShown && { 'aria-describedby': placeholderNoteId }),
        endAdornment:
          isPlaceholderWarningShown || isClearShown ? (
            <InputAdornment position='end'>
              {isPlaceholderWarningShown && (
                <Tooltip title={PLACEHOLDER_EMAIL_NOTE} placement='top'>
                  <WarningAmberRoundedIcon
                    aria-hidden
                    sx={{ fontSize: 18, color: colors.warningAlertIcon, mr: isClearShown ? 1 : 0 }}
                  />
                </Tooltip>
              )}
              {isPlaceholderWarningShown && (
                <Box component='span' id={placeholderNoteId} sx={visuallyHidden}>
                  {PLACEHOLDER_EMAIL_NOTE}
                </Box>
              )}
              {isClearShown && (
                <IconButton size='small' edge='end' aria-label={clearLabel} onClick={onClear}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              )}
            </InputAdornment>
          ) : undefined,
      }}
      sx={isChanged && !errorMessage ? editedFieldSx : undefined}
    />
  );
};

PartnerContactField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  contactField: PropTypes.shape({
    fieldKind: PropTypes.oneOf(['email', 'phoneNumber']).isRequired,
    label: PropTypes.string.isRequired,
    noun: PropTypes.string.isRequired,
    emptyPlaceholder: PropTypes.string.isRequired,
    inputType: PropTypes.string.isRequired,
    validate: PropTypes.func.isRequired,
  }).isRequired,
  onFileValue: PropTypes.string,
  hasPlaceholderOnFile: PropTypes.bool,
  isUnconfirmedRemoval: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  clearLabel: PropTypes.string.isRequired,
  onClear: PropTypes.func.isRequired,
  inputRef: PropTypes.func.isRequired,
};

PartnerContactField.defaultProps = {
  onFileValue: undefined,
  hasPlaceholderOnFile: false,
};

export default PartnerContactField;
